// ============================================================================
// CLOSING TRANSACTION SERVICE
// ============================================================================

import { get, post, put, patch, del } from '../client/axiosClient';
import { closingEndpoints } from '../endpoints/closing.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  ID,
} from '../types';
import { transformPaginatedResponse } from '../types/common.types';
import type {
  ClosingTransaction,
  CreateClosingPayload,
  UpdateClosingPayload,
  ClosingFilterParams,
  PendingClosingCheck,
} from '../types/closing.types';

class ClosingService {
  async getAll(params?: ClosingFilterParams): Promise<PaginatedResponse<ClosingTransaction>> {
    const endpoint = params
      ? buildEndpointWithQuery(closingEndpoints.list(), params)
      : closingEndpoints.list();
    const response = await get(endpoint);
    return transformPaginatedResponse<ClosingTransaction>(response.data);
  }

  async getById(id: ID): Promise<ApiResponse<ClosingTransaction>> {
    const response = await get<ApiResponse<ClosingTransaction>>(closingEndpoints.getById(id));
    return response.data;
  }

  async create(payload: CreateClosingPayload): Promise<ApiResponse<ClosingTransaction>> {
    const response = await post<ApiResponse<ClosingTransaction>>(closingEndpoints.create(), payload);
    return response.data;
  }

  async update(id: ID, payload: UpdateClosingPayload): Promise<ApiResponse<ClosingTransaction>> {
    const response = await put<ApiResponse<ClosingTransaction>>(closingEndpoints.update(id), payload);
    return response.data;
  }

  async submit(id: ID): Promise<ApiResponse<ClosingTransaction>> {
    const response = await patch<ApiResponse<ClosingTransaction>>(closingEndpoints.submit(id), {});
    return response.data;
  }

  async delete(id: ID): Promise<ApiResponse<void>> {
    const response = await del<ApiResponse<void>>(closingEndpoints.delete(id));
    return response.data;
  }

  async checkPending(mappingId: string): Promise<ApiResponse<PendingClosingCheck>> {
    const response = await get<ApiResponse<PendingClosingCheck>>(closingEndpoints.checkPending(mappingId));
    return response.data;
  }
}

export const closingService = new ClosingService();
export default closingService;
